// FE-PINV-072 — List view multi-select state.
//
// Per spec: per-row checkbox + header "select-all-visible" (NOT
// select-all-matching). Selection persists across sort + density
// toggles in the page session; clears on filter change. Shift-click
// extends the range from the last clicked row. Esc clears everything.
//
// The hook is intentionally framework-light: no React Query, no URL
// round-trip — Selection is session-scoped, not bookmarkable.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";


export interface ListSelectionApi {
  selected: Set<string>;
  count: number;
  isSelected: (id: string) => boolean;
  toggle: (id: string, opts?: { shiftKey?: boolean }) => void;
  setAllVisible: (visibleIds: readonly string[], on: boolean) => void;
  clear: () => void;
  /** Internal — list of visible row IDs in render order, used by
   *  shift-click range select. The view passes this on every render
   *  to keep the hook in sync with sort/visibility changes. */
  setVisibleOrder: (ids: readonly string[]) => void;
}


export function useListSelection(): ListSelectionApi {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const lastClickedRef = useRef<string | null>(null);
  const visibleOrderRef = useRef<readonly string[]>([]);

  const setVisibleOrder = useCallback((ids: readonly string[]) => {
    visibleOrderRef.current = ids;
  }, []);

  const clear = useCallback(() => {
    setSelected(new Set());
    lastClickedRef.current = null;
  }, []);

  // Esc clears the selection globally.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") clear();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [clear]);

  const toggle = useCallback((
    id: string, opts: { shiftKey?: boolean } = {},
  ) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (opts.shiftKey && lastClickedRef.current !== null) {
        const order = visibleOrderRef.current;
        const aIdx = order.indexOf(lastClickedRef.current);
        const bIdx = order.indexOf(id);
        if (aIdx !== -1 && bIdx !== -1) {
          const [lo, hi] = aIdx < bIdx ? [aIdx, bIdx] : [bIdx, aIdx];
          // Shift-click selects-into. Match the conventional gmail
          // behavior: the action mirrors the destination row's
          // resulting state.
          const turnOn = !prev.has(id);
          for (let i = lo; i <= hi; i += 1) {
            const rowId = order[i];
            if (turnOn) next.add(rowId);
            else next.delete(rowId);
          }
          lastClickedRef.current = id;
          return next;
        }
      }
      if (next.has(id)) next.delete(id);
      else next.add(id);
      lastClickedRef.current = id;
      return next;
    });
  }, []);

  const setAllVisible = useCallback((
    visibleIds: readonly string[], on: boolean,
  ) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) visibleIds.forEach((id) => next.add(id));
      else visibleIds.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  const isSelected = useCallback((id: string) => selected.has(id), [selected]);

  return useMemo(() => ({
    selected,
    count: selected.size,
    isSelected,
    toggle,
    setAllVisible,
    clear,
    setVisibleOrder,
  }), [selected, isSelected, toggle, setAllVisible, clear, setVisibleOrder]);
}
